<template>
  <base-page-layout title="Comparativo" class="comparativo" use-new-filters :filters="['lojas']">
    <template v-slot:header>
      <club-comparison-pdf
        :loading="loadingCharts"
        :group-by="groupBy"
        :revenue-chart-type="revenueChartType"
        :coupons-chart-type="couponsChartType"
        :ticket-chart-type="ticketChartType"
        :average-chart-type="averageChartType"
      />
    </template>
    <v-container class="px-0 pt-0" fluid grid-list-md>
      <v-layout row wrap>
        <v-flex v-for="(card, index) in cards" :key="index" xs3>
          <cbm-card
            :number-text="card.value"
            :title="card.text"
            :tooltip="card.helpText"
            :boolean-loader="loadingCards"
            :quick-action="card.quickAction"
            :is-currency="card.isCurrency"
            :percent-text="card.underText"
            :percent-description="card.description"
            unset-percentage-colors
            simple-card
          />
        </v-flex>
      </v-layout>
    </v-container>

    <v-container fluid>
      <v-layout class="d-flex align-center" row>
        <v-spacer />
        <div v-if="groupBy === 'monthly'" class="mr-3">
          <export-data :data-fetcher="csvExport" :loading="loadingCharts" filename="clube_comparativo" />
        </div>
        <mf-tabs-button v-model="groupBy" size="sm" :buttons="toggleButtons" />
      </v-layout>
    </v-container>

    <!-- Charts -->
    <div class="d-flex flex-column comparativo">
      <faturamento-chart :group-by.sync="groupBy" :loading="loadingCharts" :use-chart-type.sync="revenueChartType" />
      <cupons-chart :group-by.sync="groupBy" :loading="loadingCharts" :use-chart-type.sync="couponsChartType" />
      <ticket-medio-chart :group-by.sync="groupBy" :loading="loadingCharts" :use-chart-type.sync="ticketChartType" />
      <media-itens-cesta-chart :group-by.sync="groupBy" :loading="loadingCharts" :use-chart-type.sync="averageChartType" />
    </div>
  </base-page-layout>
</template>

<script>
import { mapState } from 'vuex'
import { mapWaitingActions } from 'vue-wait'
import BasePageLayout from '@/components/BasePageLayout.vue'
import CbmCard from '@/components/CbmCard.vue'
import { agrupamentoMensal, localeString } from '@/config/helpers.js'
// Charts
import FaturamentoChart from '@/modules/clube/desempenho/views/pages/FaturamentoChart.vue'
import CuponsChart from '@/modules/clube/desempenho/views/pages/CuponsChart.vue'
import TicketMedioChart from '@/modules/clube/desempenho/views/pages/TicketMedioChart.vue'
import MediaItensCestaChart from '@/modules/clube/desempenho/views/pages/MediaItensCestaChart.vue'
import ClubComparisonPdf from '../components/pdf/ClubComparisonPdf.vue'
import { DateTime } from 'luxon'

export default {
  components: {
    BasePageLayout,
    CbmCard,
    FaturamentoChart,
    CuponsChart,
    TicketMedioChart,
    MediaItensCestaChart,
    ClubComparisonPdf,
    ExportData: () => import('@/components/export-data/ExportData.vue'),
  },
  data: () => ({
    groupBy: 'monthly',
    cupons: [],
    loadingCards: true,
    loadingCharts: true,
    revenueChartType: 'bar',
    couponsChartType: 'bar',
    ticketChartType: 'bar',
    averageChartType: 'bar',
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
      lastUpdate: (state) => state.lastUpdate,
      comparativoCharts: (state) => state.clube.comparativoCharts,
      comparativoCards: (state) => state.clube.comparativoCards,
    }),

    toggleButtons() {
      return [
        {
          id: 0,
          label: 'Mês',
          value: 'monthly',
          active: true,
        },
        {
          id: 1,
          label: 'Semana',
          value: 'weekly',
          active: true,
        },
        {
          id: 2,
          label: 'Dia',
          value: 'daily',
          active: true,
        },
      ]
    },

    filterCharts() {
      const finalDate = DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd')
      const initialDate = finalDate.minus({ year: 1 }).set({ month: 1 })
      return {
        units: this.stateFilters.unitsConfiguration,
        group_by: this.groupBy,
        date: [initialDate.toFormat('yyyy-MM'), finalDate.toFormat('yyyy-MM')],
      }
    },
    filterCards() {
      return {
        units: this.stateFilters.unitsConfiguration,
        date: [DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('yyyy-MM')],
      }
    },
    cards() {
      return [
        {
          value: localeString(this.comparativoCards.VAL_FATURAMENTO_FIDELIDADE || 0, 0),
          text: 'Faturamento clube',
          isCurrency: true,
          loader: 'cards-comparativo',
          underText: this.getUnderText(this.comparativoCards.PER_FATURAMENTO_FIDELIDADE),
          description: this.getDescription(this.comparativoCards.PER_FATURAMENTO_FIDELIDADE, 'faturamento', 'do'),
        },
        {
          value: localeString(this.comparativoCards.VAL_TICKET_MEDIO_FIDELIDADE || 0, 2),
          text: 'Ticket médio clube',
          isCurrency: true,
          loader: 'cards-comparativo',
          underText: this.getUnderText(this.comparativoCards.PER_TICKET_MEDIO_FIDELIDADE),
          description: this.getDescription(this.comparativoCards.PER_TICKET_MEDIO_FIDELIDADE, 'ticket médio', 'do'),
        },
        {
          value: localeString(this.comparativoCards.VAL_MEDIA_ITENS_CESTA_FIDELIDADE || 0, 2),
          text: 'Média itens na cesta',
          loader: 'cards-comparativo',
          underText: this.getUnderText(this.comparativoCards.PER_MEDIA_ITENS_CESTA_FIDELIDADE),
          description: this.getDescription(this.comparativoCards.PER_MEDIA_ITENS_CESTA_FIDELIDADE, 'média', 'da'),
        },
        {
          value: localeString(this.comparativoCards.NUM_CUPONS_FIDELIDADE || 0),
          text: 'Cupons',
          loader: 'cards-comparativo',
          underText: this.getUnderText(this.comparativoCards.PER_CUPONS_FIDELIDADE),
          description: this.getDescription(this.comparativoCards.PER_CUPONS_FIDELIDADE, 'cupons', 'dos'),
        },
      ]
    },
  },
  watch: {
    groupBy() {
      this.loadingCharts = true

      this.getCharts()
    },
  },
  async mounted() {
    await this.getData()
    this.$nextTick(() => {
      this.$bus.$on('filter', this.getData)
    })
  },
  destroyed() {
    this.$bus.$off('filter')
  },
  methods: {
    ...mapWaitingActions({
      chartsComparativo: 'charts-comparativo',
      cardsComparativo: 'cards-comparativo',
    }),
    onSetCardRevenueChartType(value) {
      this.revenueChartType = value
    },
    async getData() {
      try {
        this.loadingCharts = true
        this.loadingCards = true
        await Promise.all([this.getCards(), this.getCharts()])
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingCharts = false
        this.loadingCards = false
      }
    },

    async getCards() {
      await this.cardsComparativo(this.filterCards)
    },

    async getCharts() {
      await this.chartsComparativo(this.filterCharts)
      this.loadingCharts = false
    },

    getUnderText(percent) {
      return `${localeString(Math.abs(percent), 2)}%`
    },
    getDescription(percent, variable, sufix) {
      if (percent < 0) return `abaixo ${sufix} ${variable} fora do clube`
      else return `acima ${sufix} ${variable} fora do clube`
    },
    csvExport() {
      const propertyNames = [
        'VAL_FATURAMENTO_FIDELIDADE',
        'VAL_FATURAMENTO_NAO_FIDELIDADE',
        'NUM_CUPONS_FIDELIDADE',
        'NUM_CUPONS_NAO_FIDELIDADE',
        'VAL_TICKET_MEDIO_FIDELIDADE',
        'VAL_TICKET_MEDIO_NAO_FIDELIDADE',
        'VAL_MEDIA_ITENS_CESTA_FIDELIDADE',
        'VAL_MEDIA_ITENS_CESTA_NAO_FIDELIDADE',
      ]

      const propertyResults = {}

      propertyNames.forEach((property) => {
        propertyResults[property] = agrupamentoMensal(this.comparativoCharts, property)
      })

      const labels = propertyResults[Object.keys(propertyResults)[0]].map((property) => property.data)
      const dataExport = []

      Object.entries(propertyResults).forEach((entry) => {
        const exportValues = {}
        switch (entry[0]) {
          case 'VAL_FATURAMENTO_FIDELIDADE':
            exportValues['Comparativo'] = 'Faturamento - Clube (R$)'
            break
          case 'VAL_FATURAMENTO_NAO_FIDELIDADE':
            exportValues['Comparativo'] = 'Faturamento - Fora do Clube (R$)'
            break
          case 'NUM_CUPONS_FIDELIDADE':
            exportValues['Comparativo'] = 'Cupons - Clube'
            break
          case 'NUM_CUPONS_NAO_FIDELIDADE':
            exportValues['Comparativo'] = 'Cupons - Fora do Clube'
            break
          case 'VAL_TICKET_MEDIO_FIDELIDADE':
            exportValues['Comparativo'] = 'Ticket Médio - Clube (R$)'
            break
          case 'VAL_TICKET_MEDIO_NAO_FIDELIDADE':
            exportValues['Comparativo'] = 'Ticket Médio - Fora do Clube (R$)'
            break
          case 'VAL_MEDIA_ITENS_CESTA_FIDELIDADE':
            exportValues['Comparativo'] = 'Itens na Cesta - Clube'
            break
          case 'VAL_MEDIA_ITENS_CESTA_NAO_FIDELIDADE':
            exportValues['Comparativo'] = 'Itens na Cesta - Fora do Clube'
            break
        }
        entry[1].forEach((result) => {
          exportValues[result.data] = localeString(result.valor, 2)
        })
        dataExport.push(exportValues)
      })
      const headers = ['Comparativo', ...labels]
      const rows = dataExport.map((row) => headers.map((column) => row[column]))
      const exportData = {
        headers,
        rows,
      }
      return exportData
    },
  },
}
</script>

<style lang="scss">
.comparativo {
  gap: 12px;
}
</style>
